import React, { useEffect, useState } from 'react';
import styles from './JobseekerProfileEmployerViewBody.module.css';
import { useTranslation } from 'react-i18next';
import {
    BriefcaseWhite,
    Clock,
    LanguageIcon,
    LicenseIcon,
    LocationBlue,
    MortarboardWhite,
    OverViewBG,
    PasportIcon,
    VisaIcon,
} from '../../../../assets/images/icons';
import { getTimeAgo } from '../../../../utils/formatHelpers';

function OverView({ jobseeker, ref }) {
    const { t } = useTranslation();
    const [overViewData, setOverViewData] = useState([])
    
    // GCC countries list
    const gccCountries = ['AE', 'SA', 'QA', 'KW', 'BH', 'OM'];
    
    // Check if jobseeker needs a visa (different country than nationality and not GCC citizen in GCC)
    const needsVisa = () => {
        // If country and nationality are the same, no visa needed
        if (jobseeker?.country === jobseeker?.nationality) {
            return false;
        }
        
        // If jobseeker is from a GCC country and living in a GCC country, no visa needed
        const isGccCitizen = gccCountries.includes(jobseeker?.nationality);
        const isInGccCountry = gccCountries.includes(jobseeker?.country);
        
        if (isGccCitizen && isInGccCountry) {
            return false;
        }
        
        // Otherwise, visa is needed
        return true;
    };
    
    useEffect(() => {
        const arr = [
            { title: t('overview.currentLocation'), description: t('general.country.' + jobseeker?.country), icon: LocationBlue },
            { title: t('overview.passportNationality'), description: t('general.nationality.' + jobseeker?.nationality), icon: PasportIcon },
            {
                title: t('overview.languages'), description:
                    jobseeker?.languages
                        ?.map((language) => t('general.language.' + language.languageCode))
                        .join(", "),
                icon: LanguageIcon
            },
        ]
        if (jobseeker?.driving_license) {
            arr.push(
                {
                    title: t('overview.drivingLicense'),
                    description: jobseeker?.driving_license,
                    icon: LicenseIcon,
                    isLink: true
                }
            )
        }
        
        // Add visa status only if the jobseeker needs a visa
         if (needsVisa() ) {
            arr.push({ 
                title: t('overview.visaStatus'), 
                description: 'Yes', 
                icon: VisaIcon 
            });
        }
        
        setOverViewData(arr)
    }, [jobseeker])

    const educationAvailable =
        jobseeker?.education &&
            jobseeker?.education.length > 0 &&
            jobseeker?.education[0]?.education_title ? true : jobseeker?.education[1]?.education_title ? true : false;

    let last_seen = '';
    let is_online = false;
    const date_info = getTimeAgo(new Date(jobseeker?.last_visit));
    if (date_info.unit_plural !== 'month' && date_info.unit_plural !== 'months' && date_info.unit_plural !== 'year' && date_info.unit_plural !== 'years') {

        if (date_info.unit_plural === 'second' || date_info.unit_plural === 'seconds' || date_info.unit_plural === 'minute' || (date_info.unit_plural === 'minutes' && date_info.count < 5)) {
            last_seen = t('general.UI.online');
            is_online = true;
        }
        else {
            last_seen = date_info.count + ' ' + t('general.UI.' + date_info.unit_plural) + ' ' + t('general.UI.ago');
        }

    }
    return (
        <div ref={ref} id='aboutMe' className={styles.overViewContainer}>
            <h2 className={styles.heading}>{t('overview.heading')}</h2>
            {[
                jobseeker?.jobPreferences?.length > 0 && 
                jobseeker?.jobPreferences[0]?.jobTitles && 
                jobseeker?.jobPreferences[0]?.jobTitles.filter(title => title && title.trim() !== '').length > 0 && {
                    title: t('overview.lookingFor'),
                    description: `${jobseeker?.jobPreferences[0]?.jobTitles.filter(title => title && title.trim() !== '').join(',')} role (${jobseeker?.jobPreferences[0]?.basePay}/${jobseeker?.jobPreferences[0]?.payPeriod})`,
                    icon: BriefcaseWhite,
                },
                educationAvailable && {
                    title: t('overview.education'),
                    description: jobseeker?.education[0]?.education_title ? jobseeker?.education[0]?.education_title : jobseeker?.education[1]?.education_title,
                    icon: MortarboardWhite,
                },
                { title: t('overview.lastActive'), description: last_seen, icon: Clock },
            ]
                .filter(Boolean)
                .map((item, index) => {
                    return (
                        <div className={styles.block} key={index}>
                            <div className={styles.iconContainer}>
                                <img
                                    className={styles.overViewIcon}
                                    src={item.icon}
                                    alt={item.title}
                                />
                            </div>
                            <div className={styles.blockBody}>
                                <p
                                    className={styles.description}
                                    style={{ textAlign: "left" }}
                                >
                                    {item.title}
                                </p>
                                <p className={styles.descriptionBold}>
                                    {item.description}
                                </p>
                            </div>
                        </div>
                    );
                })}
            <div className={styles.hrline} />
            {overViewData.map((item, index) => {
                return (
                    <div className={styles.block} key={index}>
                        <div className={styles.iconContainerLight}>
                            <img
                                className={styles.overViewIcon}
                                src={item.icon}
                                alt={item.title}
                            />
                        </div>
                        <div className={styles.blockBody}>
                            <p
                                className={styles.description}
                                style={{ textAlign: "left" }}
                            >
                                {item.title}
                            </p>
                            {item.isLink && item.description ? (
                                <p className={styles.descriptionBold}>
                                    <a
                                        href={process.env.REACT_APP_UPLOADS_PATH + item.description}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('overview.viewDrivingLicense')}
                                    </a>
                                </p>
                            ) : (
                                <p className={styles.descriptionBold}>{item.description}</p>
                            )}
                        </div>
                    </div>
                );
            })}
            <img className={styles.overViewBG} src={OverViewBG} alt="Overview Background" />
        </div>
    );
}

export default OverView;
